
import { Component, Vue, Prop } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import { DeviceService } from "@sureview/camera";
import { DeviceControllerOutput } from "@/store/site-monitor-cameras/types";
import { EventDetails } from "@/store/site-monitor/types";
import { SessionResource } from "@/store/sessions/types";
import { renderDistance } from "@/filters";
import api from "@/services/api.service";

const SiteMonitor = namespace("siteMonitor");
const Session = namespace("sessions");

@Component({
	filters: {
		renderDistance
	}
})
export default class ControlDeviceItem extends Vue {
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@SiteMonitor.Getter("getEventDetails") getEventDetails: EventDetails;
	@SiteMonitor.Mutation setActivity: () => void;

	@Session.Getter getSession: (resourceId: number) => any;

	@Prop({ required: true }) output!: DeviceControllerOutput;

	public get outputReasonOptional(): boolean {
		return this.getFeature(["Alarms", "MediaMatrix", "RelayControl", "ReasonOptional"]);
	}

	public pulseOutput(): void {
		this.output.controlRequest = true;

		this.$nextTick(() => {
			const refKey = `reason-${this.output.objectId}`;
			let textArea = this.$refs[refKey];

			if (!textArea) {
				return;
			}

			if (Array.isArray(textArea)) {
				textArea = textArea[0];

				if (!textArea) {
					return;
				}
			}

			if (textArea instanceof HTMLTextAreaElement) {
				textArea.focus();
			}
		});
	}

	private cancelPulse(): void {
		this.output.controlRequest = false;
		this.output.controlReason = "";
	}

	public async doPulseOutput(): Promise<void> {
		if ((!this.outputReasonOptional && (!this.output.controlReason || !this.output.controlReason.trim().length)) || this.output.sendingCommand) {
			return;
		}
		try
		{
			this.setActivity();
			this.output.sendingCommand = true;

			const device = await api.deviceAction(
				this.output.objectId,
				{
					eventId: this.getEventDetails.eventID,
					deviceAction: "pulse",
					outputReason: this.output.controlReason
				}
			);

			const auth = this.getSession(SessionResource.DeviceServiceSession);
			const deviceService = new DeviceService(api.getDeviceServiceAddress(), device.deviceServerEndpoint);

			await deviceService.sendOutputCmd(auth, this.output.objectId, "output", "pulse").then(
				() => {
					this.output.controlRequest = false;
					this.output.controlReason = "";
					this.output.sendingCommand = false;
				},
				error => {
					console.error(error);
					this.output.controlError = error ? error.statusText : "Error triggering relay";
					this.output.sendingCommand = false;
				}
			);
		}
		catch(error)
		{
			console.error(error);
			this.output.controlError = error ? error.statusText : "Error triggering relay";
			this.output.sendingCommand = false;
			return;
		}

	}

	public async setOutput(on: boolean): Promise<void> {
		const action = on ? "on" : "off";

		const device = await api.deviceAction(
			this.output.objectId,
			{
				eventId: this.getEventDetails.eventID,
				deviceAction: action,
				outputReason: this.output.controlReason
			}
		);

		const auth = this.getSession(SessionResource.DeviceServiceSession);
		const deviceService = new DeviceService(api.getDeviceServiceAddress(), device.deviceServerEndpoint);

		await deviceService.sendOutputCmd(auth, this.output.objectId, "output", action).then(
			() => {
				this.output.controlRequest = false;
				this.output.controlReason = "";
				this.output.sendingCommand = false;
			},
			error => {
				console.error(error);
				this.output.controlError = error ? error.statusText : "Error triggering relay";
				this.output.sendingCommand = false;
			}
		);
	}

	public isOutputDisabled(output: DeviceControllerOutput): boolean {
		return !this.outputReasonOptional && (!output.controlReason || !output.controlReason.trim().length);
	}
}
